import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { CriteriaTemplate } from '../../campaign-wizard.types';
import { wz } from '../../components/wizard-tokens';
import { WizGradientButton } from '../../components/wizard-ui';
import { findSameName, TEMPLATE_NAME_MAX } from './judging-logic';

interface Props {
  open: boolean;
  /** Vorgeschlagener Name (Vorlage, Jobtitel oder Kampagnenname). */
  initialName: string;
  templates: CriteriaTemplate[];
  busy: boolean;
  /** Fehler vom Speichern (z. B. 409), im Dialog angezeigt. */
  error: string | null;
  onCancel: () => void;
  onSave: (name: string, scope: 'team' | 'own') => void;
}

/** „Als Vorlage speichern“: Name vergeben, für das Team oder nur für mich. */
const SaveTemplateDialog: React.FC<Props> = ({
  open,
  initialName,
  templates,
  busy,
  error,
  onCancel,
  onSave,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [name, setName] = useState(initialName);
  const [scope, setScope] = useState<'team' | 'own'>('team');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initialName.slice(0, TEMPLATE_NAME_MAX));
      setScope('team');
      setTouched(false);
    }
  }, [open]);

  const trimmed = name.trim();
  const overwrite = findSameName(templates, trimmed);
  const submit = () => {
    setTouched(true);
    if (!trimmed || busy) return;
    onSave(trimmed, scope);
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle sx={{ fontSize: 20, fontWeight: 700 }}>{t('wizJudgSaveTitle')}</DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: '14px', paddingTop: '8px !important' }}
      >
        <label className={classes.label}>
          {t('wizJudgSaveName')}
          <input
            className={classes.input}
            value={name}
            maxLength={TEMPLATE_NAME_MAX}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            onChange={e => setName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
          />
          <span className={classes.counter} data-testid="wiz-judg-name-counter">
            {name.length}/{TEMPLATE_NAME_MAX}
          </span>
        </label>
        {touched && !trimmed ? (
          <div className={classes.error}>{t('wizJudgSaveNameEmpty')}</div>
        ) : null}
        {overwrite && trimmed ? (
          <div className={classes.warn} data-testid="wiz-judg-overwrite">
            {t('wizJudgSaveOverwrite')}
          </div>
        ) : null}
        <div className={classes.scopes} role="radiogroup">
          {(['team', 'own'] as const).map(s => (
            <label key={s} className={classes.scope}>
              <input
                type="radio"
                name="wiz-judg-scope"
                checked={scope === s}
                onChange={() => setScope(s)}
              />
              {t(s === 'team' ? 'wizJudgSaveTeam' : 'wizJudgSaveOwn')}
            </label>
          ))}
        </div>
        {error ? <div className={classes.error}>{error}</div> : null}
      </DialogContent>
      <DialogActions sx={{ padding: '0 24px 20px', gap: '12px' }}>
        <button type="button" className={classes.cancel} onClick={onCancel} disabled={busy}>
          {t('wizJudgSaveCancel')}
        </button>
        <WizGradientButton onClick={submit} disabled={!trimmed || busy}>
          {busy ? t('wizJudgSaveBusy') : t('wizJudgSaveConfirm')}
        </WizGradientButton>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()({
  label: { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14, fontWeight: 600 },
  input: {
    height: 40,
    boxSizing: 'border-box',
    borderRadius: 8,
    border: `1px solid ${wz.border}`,
    padding: '0 12px',
    fontSize: 15,
    fontFamily: 'inherit',
    color: wz.text,
  },
  counter: { alignSelf: 'flex-end', fontSize: 12, fontWeight: 400, color: wz.muted },
  scopes: { display: 'flex', gap: 20 },
  scope: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' },
  error: { fontSize: 13, color: wz.redFg },
  warn: { fontSize: 13, color: wz.warnFg },
  cancel: {
    height: 40,
    padding: '0 20px',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    color: wz.primary,
    background: '#FFF',
    border: `1.5px solid ${wz.primary}`,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
});

export default SaveTemplateDialog;
